/**
 * MasterActionListView (SK-MASTER, T-19, R-1g-SKILL3-SURFACE) — Skill #2
 * `read_master_list` collapsed read. Chronological (raise order —
 * distinct from Decisions' walk-of-shame age ordering), manual
 * criticality, trade filter ("painter's list"). DISTINCT from the
 * Skill-#1 daily/triage stream (DailyLog, T-17) — the surface-gap
 * R-1g-SKILL3-SURFACE requires two separate screens, not one merged view.
 *
 * §7.M5 (Divergence-Memo reconciliation): the list matches the preview —
 * title "Master List", **trade filter pills** (derived from the items' own
 * distinct trades, not a free-text box), rows tappable (done → 0.45 opacity),
 * status shown as the lowercase label. The per-row criticality `<select>` is
 * RETIRED — status + criticality are now set on the row's **detail** surface
 * (`master-item-detail`, nested in this SK-MASTER section — single entry point,
 * so it stays internal, unlike the log's app-level save screen). On Save the
 * detail emits ONE supersede carrier (app-level `onCommitStatus` →
 * `buildCarrierFields`/`saveToProject`) mirroring status.rs's
 * `build_supersede_carrier` FSM: `can_do → {cannot_do(+blocker), done}`,
 * `cannot_do → {can_do, cannot_do(+blocker), done}`, `done` terminal (no
 * reopen); `cannot_do` requires a non-empty blocker.
 *
 * `MasterItem` mirrors goalkeeper's `master_action_list::readview::
 * MasterItem` 1:1 (snake_case wire fields, kept verbatim — consumed read
 * model, never round-tripped). Consumes an already-collapsed `MasterItem[]`
 * (LIVE read transport GATED per PIN-1g→1l-PWA-TRANSPORT/S8; Stage A runs
 * against seeded-DB read fixtures — so a Save emits a carrier to the outbox
 * for the fold, it does NOT optimistically mutate this seeded read model).
 * This is Lock 1 of AC-1g-28 (T-16): the only input accepted is a typed
 * `MasterItem[]`, never a raw row.
 */

import { useMemo, useState } from "preact/hooks";
import { BackLink } from "./BackLink";
import { AuthenticatedPhoto, type PhotoFetcher } from "./AuthenticatedPhoto";
import { photoMediaRefs } from "../reports/report-client";
import { presentPhases } from "../read/tag-filter";
import { Chip } from "./Chip";
import { PillButton } from "./PillButton";

export type StatusValue = "can_do" | "cannot_do" | "done";
export type ItemType = "action" | "decision";

export interface MasterItem {
  id: string;
  author?: string;
  author_name?: string;
  text: string;
  room?: string;
  description?: string;
  source_refs: string[];
  status: StatusValue;
  blocker?: string;
  criticality_tier?: string;
  item_type?: ItemType;
  owner?: string;
  assignee?: string;
  trade?: string;
  decides?: string;
  sub?: string;
  /** M-TAG1 (BUI-92): canonical master-decision-list phase id ("0".."9"). */
  phase?: string;
  first_raised_ts: string; // ISO
}

/** The status change a Save commits — mirrors status.rs's carrier meta
 * (status + optional blocker) plus the manually-set criticality tier, which
 * rides the same carrier's payload bag. */
export interface StatusChange {
  status: StatusValue;
  blocker?: string;
  criticality_tier?: string;
  /** Responsible Party. Rides the same carrier bag as criticality and resolves
   * per-field on the INV-4 chain — absent means unchanged. */
  owner?: string;
  /** Trade. Same carrier bag, same per-field resolution. Setting this is also what
   * makes the trade FILTER work: the pills are derived from the items' trades, and
   * nothing else populates them. */
  trade?: string;
  /** The status THIS SCREEN was showing when the human hit Save — not what they
   * are asserting, which is `status` above.
   *
   * The two are usually equal and that is the whole point: a Save that only
   * changed the owner still has to send a `status`, and without a second value to
   * compare it against, the server cannot tell that assertion from a deliberate
   * one. See `resolve_against_base` (master_action_list/mod.rs). */
  base_status?: StatusValue;
}

/** The read-path domain filter. Axes are AND-combined; absent axes match all. */
export interface ReadFilter {
  phase?: string;
  trade?: string;
  sub?: string;
  criticality_tier?: string;
  item_type?: ItemType;
  status?: StatusValue;
}

export function applyReadFilter(items: MasterItem[], filter: ReadFilter): MasterItem[] {
  return items.filter((item) => {
    if (filter.phase !== undefined && item.phase !== filter.phase) return false;
    if (filter.trade !== undefined && item.trade !== filter.trade) return false;
    if (filter.sub !== undefined && item.sub !== filter.sub) return false;
    if (filter.criticality_tier !== undefined && item.criticality_tier !== filter.criticality_tier) return false;
    if (filter.item_type !== undefined && item.item_type !== filter.item_type) return false;
    if (filter.status !== undefined && item.status !== filter.status) return false;
    return true;
  });
}

/** Chronological (raise order) — the PRIMARY ordering here, distinct from
 * DecisionLogView's age-ranked walk-of-shame (both happen to sort by
 * `first_raised_ts` ascending, but this is master-list's own ordering
 * rule, not a shared import, since R-1g-SKILL3-SURFACE requires the two
 * surfaces stay structurally distinct). */
export function chronological(items: MasterItem[]): MasterItem[] {
  return [...items].sort(
    (a, b) => new Date(a.first_raised_ts).getTime() - new Date(b.first_raised_ts).getTime(),
  );
}

/** The lowercase, spaced status label the preview shows on a row
 * ("can_do" → "can do"). */
function statusLabel(status: StatusValue): string {
  return status.replace("_", " ");
}

export interface MasterActionListViewProps {
  items: MasterItem[];
  filter?: ReadFilter;
  fetchPhoto?: PhotoFetcher;
  /** §7.M5: a row's detail Save commits a status/criticality change. The app
   * mints the supersede carrier (project_ref/tenant_id live there, AC-1g-07). */
  onCommitStatus: (item: MasterItem, change: StatusChange) => void;
  /** §align: selected project's visible name — header subtitle. */
  projectLabel?: string;
  /** §align: bottom-of-list "Share with sub" (coming-soon at Stage A). */
  onShareWithSub?: () => void;
}

const STATUS_CHIPS: { v: StatusValue; label: string }[] = [
  { v: "can_do", label: "Can do" },
  { v: "cannot_do", label: "Cannot do" },
  { v: "done", label: "Done" },
];
// The detail's criticality vocabulary — label "Critical" maps to tier `high`
// (matching the row pill's data-criticality="high" and the seed data).
const CRIT_CHIPS: { v: string; label: string }[] = [
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "Critical" },
];

function MasterItemDetail({
  item,
  onBack,
  onCommit,
}: {
  item: MasterItem;
  onBack: () => void;
  onCommit: (change: StatusChange) => void;
}) {
  const [status, setStatus] = useState<StatusValue>(item.status);
  const [blocker, setBlocker] = useState<string>(item.blocker ?? "");
  const [crit, setCrit] = useState<string>(item.criticality_tier ?? "");
  // Responsible Party was RENDERED here but had no input, so it was the one field
  // on this surface that could not be changed (Red, field test 2026-07-29: "I can
  // edit everything but the responsible party"). The whole chain below it already
  // existed — `owner` is in the skill's input schema, `readview.rs` resolves it
  // per-field on the INV-4 chain exactly like criticality, and the signed card
  // carries a NULL input_schema, so nothing here can move a signed digest.
  const [owner, setOwner] = useState<string>(item.owner ?? "");
  // Trade had the same gap as Responsible Party — rendered in the meta line, never
  // editable (Red, 2026-07-30). It is also the reason the list showed only an "All"
  // filter: the pills are derived from the items' `trade` values and every item was
  // null, so there was nothing to derive. This control is what populates them.
  const [trade, setTrade] = useState<string>(item.trade ?? "");

  // `done` is terminal (status.rs FSM: no reopen) — lock the whole surface.
  const isDone = item.status === "done";
  const needsBlocker = status === "cannot_do" && blocker.trim().length === 0;
  const saveDisabled = isDone || needsBlocker;

  /** Send ONLY what this human actually changed.
   *
   * Every control here is pre-filled from `item`, which is a snapshot from the
   * last server read — and the dispatcher only wakes on a 30s timer, so it can be
   * half a minute stale. Sending the whole form back meant that editing one field
   * silently RE-ASSERTED every other field as you last saw it, reverting a
   * colleague's change to something you never touched. With two supers on one
   * project (Red, 2026-07-30) that is the difference between per-field merge and
   * quiet data loss.
   *
   * Absent means UNCHANGED the whole way down the INV-4 chain (readview.rs
   * resolves each field with `if j.<field>.is_some()`), so omitting a field is
   * precisely "leave whatever is there alone". Dirty-tracking is what turns that
   * from a coincidence into a guarantee.
   *
   * `status` is the ONE exception and rides along even when unchanged:
   * `is_master_status_carrier` requires it to recognise the carrier at all
   * (supersedes_ref + status + change_ordinal). Omitting it is therefore not
   * available, so instead we send what this screen was SHOWING alongside what it
   * asserts — `base_status` — and let the server tell the two apart. A stale echo
   * is re-based onto the current status rather than applied over it; a deliberate
   * change made against a stale reading is refused.
   *
   * `item.status` IS the base: every control here is seeded from `item`, which is
   * the snapshot this human made their decision on. Nothing new has to be tracked
   * for it, which is also what makes it trustworthy — a separately-maintained copy
   * could drift from what is actually on screen.
   */
  function dirtyChange(): StatusChange {
    const change: StatusChange = { status, base_status: item.status };
    // `blocker` rides whenever the status IS cannot_do, changed or not: the FSM
    // requires a non-empty blocker for that state, so omitting it as "unchanged"
    // would produce a carrier the skill refuses.
    if (status === "cannot_do") change.blocker = blocker.trim();
    if ((crit || undefined) !== item.criticality_tier) change.criticality_tier = crit || undefined;
    // An emptied box means UNCHANGED, never "" — asserting a blank would clear a
    // name rather than leave it alone. Clearing outright is a gesture we do not
    // have yet, and doing it by accident would be worse than not offering it.
    const nextOwner = owner.trim();
    if (nextOwner && nextOwner !== (item.owner ?? "")) change.owner = nextOwner;
    // Lower-cased so the derived filter pills cannot split "Framing" and "framing"
    // into two trades meaning the same thing.
    const nextTrade = trade.trim().toLowerCase();
    if (nextTrade && nextTrade !== (item.trade ?? "")) change.trade = nextTrade;
    return change;
  }

  return (
    <div data-testid="master-item-detail" style="display:flex;flex-direction:column;gap:16px">
      <BackLink label="Action List" testid="master-item-detail-back" onBack={onBack} />

      <p style="margin:0;font-size:20px;font-weight:700;color:var(--mark-text-primary)">{item.text}</p>
      <p data-testid="master-item-meta" style="margin:0;font-size:11.5px;color:var(--mark-text-muted)">
        {(item.author_name || item.author) && <span>Filed by {item.author_name || item.author}</span>}
        {item.owner && <span> · Responsible Party: {item.owner}</span>}
        {item.trade && <span> · {item.trade}</span>}
        {item.room && <span> · Room: {item.room}</span>}
        {item.decides && <span> · Decision maker: {item.decides}</span>}
      </p>

      <div>
      <p class="save-chip-group-hint">Status</p>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px" data-testid="master-item-status-chips" role="group">
        {STATUS_CHIPS.map((c) => (
          <Chip
            key={c.v}
            data-testid={`master-item-status-${c.v}`}
            aria-pressed={status === c.v}
            disabled={isDone}
            onClick={() => setStatus(c.v)}
          >
            {c.label}
          </Chip>
        ))}
        </div>
        {isDone && (
          <p data-testid="master-item-done-lock" style="margin:8px 0 0;font-size:12px;color:var(--mark-text-muted)">Done is final — recapture it if the work reopens.</p>
        )}
      </div>

      {status === "cannot_do" && (
        <label>
          What's blocking it? (required)
          <textarea
            data-testid="master-item-blocker"
            placeholder="e.g. waiting on window delivery"
            value={blocker}
            onInput={(e) => setBlocker((e.target as HTMLTextAreaElement).value)}
          />
        </label>
      )}

      <label>
        Responsible Party
        <input
          type="text"
          data-testid="master-item-owner"
          placeholder="e.g. Miguel"
          value={owner}
          disabled={isDone}
          onInput={(e) => setOwner((e.target as HTMLInputElement).value)}
        />
      </label>

      <label>
        Trade
        <input
          type="text"
          data-testid="master-item-trade"
          placeholder="e.g. framing"
          value={trade}
          disabled={isDone}
          onInput={(e) => setTrade((e.target as HTMLInputElement).value)}
        />
      </label>

      <div>
      <p class="save-chip-group-hint">Criticality</p>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px" data-testid="master-item-criticality-chips" role="group">
        {CRIT_CHIPS.map((c) => (
          <Chip
            key={c.v}
            data-testid={`master-item-criticality-${c.v}`}
            aria-pressed={crit === c.v}
            disabled={isDone}
            onClick={() => setCrit(c.v)}
          >
            {c.label}
          </Chip>
        ))}
        </div>
      </div>

      <PillButton
        fullWidth
        data-testid="master-item-save"
        disabled={saveDisabled}
        onClick={() =>
          onCommit(dirtyChange())
        }
      >
        Save changes
      </PillButton>
    </div>
  );
}

export function MasterActionListView({ items, filter, fetchPhoto, onCommitStatus, projectLabel, onShareWithSub }: MasterActionListViewProps) {
  const [trade, setTrade] = useState<string>("all");
  const [phase, setPhase] = useState<string>("all");
  const [detailId, setDetailId] = useState<string | null>(null);

  const effectiveFilter: ReadFilter = useMemo(
    () => ({
      ...(filter ?? {}),
      ...(trade !== "all" ? { trade } : {}),
      ...(phase !== "all" ? { phase } : {}),
    }),
    [filter, trade, phase],
  );

  const visible = useMemo(
    () => chronological(applyReadFilter(items, effectiveFilter)),
    [items, effectiveFilter],
  );

  // Trade pills derived from the items' own distinct trades (the preview's
  // Framer/Painter/Electrician are mock data — real trades come from the read
  // model). "all" always first.
  const tradeIds = useMemo(
    () => ["all", ...Array.from(new Set(items.map((i) => i.trade).filter((t): t is string => !!t)))],
    [items],
  );
  const phaseOptions = useMemo(() => presentPhases(items), [items]);

  const detailItem = detailId != null ? items.find((i) => i.id === detailId) ?? null : null;

  if (detailItem) {
    return (
      <section data-skin-slot="SK-MASTER" data-testid="master-action-list-view">
        <MasterItemDetail
          key={detailItem.id}
          item={detailItem}
          onBack={() => setDetailId(null)}
          onCommit={(change) => {
            onCommitStatus(detailItem, change);
            setDetailId(null);
          }}
        />
      </section>
    );
  }

  return (
    <section data-skin-slot="SK-MASTER" data-testid="master-action-list-view">
      <div>
        <div style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">Action List</div>
        {projectLabel && (
          <div style="font-size:11.5px;color:var(--mark-text-muted);margin-top:8px;line-height:1.4">All open items · {projectLabel}</div>
        )}
      </div>
      <div class="log-filters" data-testid="master-list-filters" role="tablist">
        {tradeIds.map((t) => (
          <button
            key={t}
            type="button"
            class="log-filter-pill"
            data-testid={`master-list-filter-${t}`}
            aria-pressed={trade === t}
            onClick={() => setTrade(t)}
          >
            {t === "all" ? "All" : t}
          </button>
        ))}
      </div>
      {phaseOptions.length > 0 && (
        <div class="log-filters" data-testid="master-list-phase-filters" role="tablist" aria-label="Phase">
          <button
            type="button"
            class="log-filter-pill"
            data-testid="master-list-phase-all"
            aria-pressed={phase === "all"}
            onClick={() => setPhase("all")}
          >
            All phases
          </button>
          {phaseOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              class="log-filter-pill"
              data-testid={`master-list-phase-${option.id}`}
              aria-pressed={phase === option.id}
              onClick={() => setPhase(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <ul data-testid="master-list-items">
        {visible.map((item) => (
          <li
            key={item.id}
            data-testid={`master-list-row-${item.id}`}
            style={`cursor:pointer;opacity:${item.status === "done" ? "0.45" : "1"}`}
            onClick={() => setDetailId(item.id)}
          >
            <div class="master-row-main">
              <span data-testid={`master-list-row-text-${item.id}`}>{item.text}</span>
              <div class="inherited-photo-strip">
                {photoMediaRefs(item.source_refs).map((mediaRef, index) => (
                  <AuthenticatedPhoto
                    key={mediaRef}
                    mediaRef={mediaRef}
                    alt={`Photo ${index + 1} attached to action: ${item.text}`}
                    testid={`master-list-row-photo-${item.id}-${index}`}
                    fetchPhoto={fetchPhoto}
                    className="inherited-photo-thumb"
                  />
                ))}
              </div>
              <p class="master-row-meta">
                {item.owner && <span class="master-row-owner"> · Responsible Party: {item.owner}</span>}
                {item.room && <span> · Room: {item.room}</span>}
                {item.decides && <span> · Decision maker: {item.decides}</span>}
              </p>
            </div>
            <div class="master-row-side">
              {item.criticality_tier === "high" && (
                <span class="criticality-pill" data-criticality="high">
                  Critical
                </span>
              )}
              <span class="master-row-status" data-testid={`master-list-row-status-${item.id}`}>
                {statusLabel(item.status)}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {onShareWithSub && (
        <Chip style="width:100%;height:52px;border-radius:26px" onClick={onShareWithSub}>
          Share with sub
        </Chip>
      )}
    </section>
  );
}
